import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Input, Select, Textarea } from '../../components/ui/FormField.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { useBackNavigation } from '../../hooks/useBackNavigation.js';
import { useSaveMutation } from '../../hooks/useSaveMutation.js';
import { api } from '../../services/api.js';
import { RESOURCE_KEYS, type ResourceName } from '../../services/resourceKeys.js';

export interface TaxonomyField {
  name: string;
  label: string;
  type: 'text' | 'number' | 'textarea' | 'select';
  required?: boolean;
  hint?: string;
  // For 'select': static options, or a loader (e.g. brands for a model's parent).
  options?: Array<{ value: string; label: string }>;
  optionsQuery?: { resource: ResourceName; apiPath: string };
}

export interface TaxonomyFormConfig {
  title: string; // "Brand"
  apiPath: string; // '/car-brands'
  basePath: string; // '/car-brands'
  /**
   * 161: which resource this form saves. Replaces the free-text `queryKey` it used to carry — a
   * string could be misspelled into a cache of its own, and this union cannot.
   */
  resource: ResourceName;
  fields: TaxonomyField[]; // beyond name+status (always present)
}

interface OptionSource {
  _id: string;
  name: string;
}

/**
 * Shared admin create/edit form for a global taxonomy entity. Always renders a
 * Name text field + a Status select; `config.fields` adds entity-specific
 * fields (description, sort_order, icon, brand select). Number fields submit as
 * numbers; empty optional fields are omitted.
 */
export function TaxonomyFormPage({ config }: { config: TaxonomyFormConfig }) {
  const navigate = useNavigate();
  const goBack = useBackNavigation(config.basePath);
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form, setForm] = useState<Record<string, string>>({ name: '', status: 'active' });
  const [error, setError] = useState('');

  const { data: existing } = useQuery({
    queryKey: RESOURCE_KEYS[config.resource].one(id as string),
    queryFn: () => api.get<Record<string, unknown>>(`${config.apiPath}/${id}`),
    enabled: isEdit,
  });

  // Load dynamic select options (e.g. brands for the model form).
  const dynamicField = config.fields.find((f) => f.optionsQuery);
  const { data: optionRows } = useQuery({
    queryKey: dynamicField?.optionsQuery
      ? RESOURCE_KEYS[dynamicField.optionsQuery.resource].list
      : ['noop'],
    queryFn: () => api.get<OptionSource[]>(dynamicField?.optionsQuery?.apiPath ?? ''),
    enabled: Boolean(dynamicField),
  });

  useEffect(() => {
    if (!existing) return;
    const next: Record<string, string> = {
      name: String(existing.name ?? ''),
      status: String(existing.status ?? 'active'),
    };
    for (const f of config.fields) {
      const v = existing[f.name];
      if (v != null) next[f.name] = String(v);
    }
    setForm(next);
  }, [existing, config.fields]);

  function buildPayload(): Record<string, unknown> {
    const out: Record<string, unknown> = { name: form.name, status: form.status };
    for (const f of config.fields) {
      const raw = form[f.name];
      if (raw === undefined || raw === '') continue;
      out[f.name] = f.type === 'number' ? Number(raw) : raw;
    }
    return out;
  }

  // 161 (US2): this form invalidated NOTHING, so after a save the list it returns to kept showing
  // the old row and reopening the record kept showing the old values — on all four taxonomy
  // screens at once, because they share this file.
  const { save, isPending } = useSaveMutation({
    resource: config.resource,
    id,
    mutationFn: () =>
      isEdit
        ? api.patch(`${config.apiPath}/${id}`, buildPayload())
        : api.post(config.apiPath, buildPayload()),
    onSaved: () => navigate(config.basePath),
    onError: (err) =>
      setError(
        (err as { message?: string }).message ?? `Failed to save ${config.title.toLowerCase()}`,
      ),
  });

  function set(k: string, v: string) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  return (
    <div>
      <PageHeader
        title={isEdit ? `Edit ${config.title}` : `New ${config.title}`}
        description={`${isEdit ? 'Update' : 'Create'} a ${config.title.toLowerCase()} in the catalog.`}
        fallback={config.basePath}
      />
      <form
        className="max-w-2xl space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          setError('');
          void save();
        }}
      >
        <Card>
          <div className="space-y-4">
            <FormField label="Name" name="name" required>
              <Input
                id="name"
                value={form.name}
                onInput={(e) => set('name', (e.target as HTMLInputElement).value)}
                required
              />
            </FormField>

            {config.fields.map((f) => {
              const options =
                f.options ?? (optionRows ?? []).map((o) => ({ value: o._id, label: o.name }));
              return (
                <FormField
                  key={f.name}
                  label={f.label}
                  name={f.name}
                  required={f.required}
                  hint={f.hint}
                >
                  {f.type === 'textarea' ? (
                    <Textarea
                      id={f.name}
                      rows={2}
                      value={form[f.name] ?? ''}
                      onInput={(e) => set(f.name, (e.target as HTMLTextAreaElement).value)}
                    />
                  ) : f.type === 'select' ? (
                    <Select
                      id={f.name}
                      value={form[f.name] ?? ''}
                      onChange={(e) => set(f.name, (e.target as HTMLSelectElement).value)}
                      required={f.required}
                    >
                      <option value="" disabled>
                        Select…
                      </option>
                      {options.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      id={f.name}
                      type={f.type === 'number' ? 'number' : 'text'}
                      value={form[f.name] ?? ''}
                      onInput={(e) => set(f.name, (e.target as HTMLInputElement).value)}
                      required={f.required}
                    />
                  )}
                </FormField>
              );
            })}

            <FormField label="Status" name="status">
              <Select
                id="status"
                value={form.status}
                onChange={(e) => set('status', (e.target as HTMLSelectElement).value)}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Select>
            </FormField>
          </div>
        </Card>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex gap-3">
          <Button type="submit" loading={isPending}>
            {isEdit ? 'Save Changes' : `Create ${config.title}`}
          </Button>
          <Button type="button" variant="ghost" onClick={goBack}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
